import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { createSelector } from "reselect";
import { useLocation, useNavigate } from "react-router-dom";
import { UserSquare2 } from "lucide-react";

import {
  getChat,
  getDetailContact,
  deleteContact,
  getContact,
} from "slices/thunk";
import DeleteModal from "Common/DeleteModal";
import { getCount } from "services/contact.service";
import { getByContactId } from "services/conversation.service";
import { cn } from "lib/utils";

import ContactList from "./ContactList";
import ContactDetail from "./ContactDetail";
import ContactForm from "./ContactForm";

const Contact = () => {
  document.title = `Contact | EngageHub IQ`;
  const navigate = useNavigate();
  const location = useLocation();
  const fromPanelOpen =
    (location.state as { fromPanelOpen?: boolean } | undefined)
      ?.fromPanelOpen || false;

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showContactForm, setShowContactForm] = useState(false);
  const [showContactDetail, setShowContactDetail] = useState(false);
  const [showContactList, setShowContactList] = useState(true);
  const [contacts, setContacts] = useState<any[]>([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [searchContactText, setSearchContactText] = useState("");
  const [skipCount, setSkipCount] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [count, setCount] = useState(0);
  const [contact, setContact] = useState<any>(null);
  const [showContactInfo, setShowContactInfo] = useState(false);
  const [conversations, setConversations] = useState<any[]>([]);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const limit = 20;
  const dispatch = useDispatch<any>();

  const selectContactState = createSelector(
    (state: any) => state.Contact,
    (c) => ({ contactDetail: c.contactDetail, error: c.error })
  );

  const { contactDetail } = useSelector(selectContactState);
  const allChats = useSelector((state: any) => state.Chat.chats);

  useEffect(() => {
    dispatch(getChat());
  }, [dispatch]);

  useEffect(() => {
    setConversations(allChats);
  }, [allChats]);

  useEffect(() => {
    fetchCount();
    loadContacts(0, true, searchContactText);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchContactText]);

  useEffect(() => {
    if (contactDetail?.id) {
      setContact(contactDetail);
      setShowContactDetail(true);
      setShowContactForm(false);
      if (window.innerWidth < 1280 && showContactInfo) {
        setShowContactList(false);
      }
      if (window.innerWidth < 1280 && fromPanelOpen) {
        setShowContactList(false);
      }
    }
  }, [contactDetail, showContactInfo, fromPanelOpen]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1280) setShowContactList(true);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const fetchCount = async () => {
    getCount()
      .then((res: any) => setCount(typeof res === "number" ? res : 0))
      .catch(() => setCount(0));
  };

  const loadContacts = async (skip = 0, reset = false, keyword = "") => {
    setLoadingContacts(true);
    const res = await dispatch(
      getContact({ skipCount: skip, maxResultCount: limit, filter: keyword })
    );
    setLoadingContacts(false);
    const newContacts = res.payload || [];
    if (newContacts.length < limit) setHasMore(false);
    setContacts((prev) =>
      reset ? newContacts : [...prev, ...newContacts]
    );
    setSkipCount((prev) => (reset ? limit : prev + limit));
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchContactText(e.target.value);
    setHasMore(true);
    setSkipCount(0);
  };

  const handleContactSelect = async (c: any) => {
    setLoadingDetail(true);
    setShowContactInfo(true);
    await dispatch(getDetailContact(c.id));
    setLoadingDetail(false);
  };

  const handleBackToContactList = () => {
    setShowContactList(true);
    setShowContactDetail(false);
    setShowContactForm(false);
  };

  const handleChatOpen = async () => {
    let conversationId: string | undefined;
    const existing = Array.isArray(conversations)
      ? conversations.find((c: any) => c.contactId === contactDetail.id)
      : undefined;

    if (existing) {
      conversationId = existing.id;
    } else {
      try {
        const foundConversation = await getByContactId(contactDetail.id);
        if (foundConversation?.id) conversationId = foundConversation.id;
      } catch {
        // ignore
      }
    }

    navigate("/conversation", {
      state: {
        fromChatOpen: true,
        conversationId,
        contactId: contactDetail.id,
      },
    });
  };

  const confirmDelete = () => {
    dispatch(deleteContact(contactDetail?.id));
    setShowDeleteModal(false);
    setContacts((prev) => prev.filter((c) => c.id !== contactDetail?.id));
    handleBackToContactList();
  };

  const handleAddContact = () => {
    setShowContactForm(true);
    if (window.innerWidth < 1280) setShowContactList(false);
  };

  const handleCancelContactForm = () => {
    setShowContactForm(false);
    setShowContactList(true);
  };

  const handleAddContactSuccess = (c: any) => {
    setContact(c);
    setContacts((prev) => [c, ...prev]);
    setShowContactForm(false);
    setShowContactList(true);
  };

  return (
    <div className="-m-4 sm:-m-6 lg:-m-8">
      <div
        className={cn(
          "flex h-[calc(100vh-4rem)] w-full overflow-hidden",
          "border-y border-border bg-background xl:rounded-2xl xl:border"
        )}
      >
        <div
          className={cn(
            "xl:block xl:w-80 xl:shrink-0 xl:border-r xl:border-border",
            showContactList ? "block w-full" : "hidden"
          )}
        >
          <ContactList
            contacts={contacts}
            loading={loadingContacts}
            count={count}
            hasMore={hasMore}
            onContactSelect={handleContactSelect}
            onAddContact={handleAddContact}
            onSearch={handleSearch}
            onClearSearch={() => setSearchContactText("")}
            onLoadMore={() =>
              loadContacts(skipCount, false, searchContactText)
            }
            searchContactText={searchContactText}
            selectedChatId={
              window.innerWidth >= 1280 ? contactDetail?.id : null
            }
          />
        </div>

        {contactDetail?.id && showContactDetail && !showContactForm ? (
          <div
            className={cn(
              "min-w-0 flex-1",
              !showContactList ? "block w-full" : "hidden xl:block"
            )}
          >
            <ContactDetail
              contactDetail={contact}
              loadingDetail={loadingDetail}
              onDelete={() => setShowDeleteModal(true)}
              onChatOpen={handleChatOpen}
              onBackToContactList={handleBackToContactList}
            />
          </div>
        ) : null}

        {showContactForm ? (
          <div
            className={cn(
              "min-w-0 flex-1",
              !showContactList ? "block w-full" : "hidden xl:block"
            )}
          >
            <ContactForm
              onCancel={handleCancelContactForm}
              onSubmit={handleAddContactSuccess}
              onBackToContactList={handleCancelContactForm}
            />
          </div>
        ) : null}

        {!contactDetail?.id && !showContactForm ? (
          <div className="hidden flex-1 items-center justify-center text-center xl:flex">
            <div className="max-w-sm space-y-3 px-6">
              <div className="mx-auto grid h-14 w-14 place-items-center rounded-2xl bg-primary/10 text-primary">
                <UserSquare2 className="h-6 w-6" />
              </div>
              <h3 className="text-base font-semibold text-foreground">
                Select a contact
              </h3>
              <p className="text-sm text-muted-foreground">
                Choose someone from your directory to view their profile, or
                add a new contact.
              </p>
            </div>
          </div>
        ) : null}

        <DeleteModal
          title="Are you sure you want to proceed?"
          content="This action will also permanently remove all conversations linked to this contact. This action cannot be undone."
          show={showDeleteModal}
          onHide={() => setShowDeleteModal(false)}
          onDelete={confirmDelete}
        />
      </div>
    </div>
  );
};

export default Contact;
